import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Loader2, AlertTriangle, Download, ArrowRight, Award, Mail,
  Key, FileDown, Scale,
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    if (polledRef.current) return;
    polledRef.current = true;

    const poll = async (n = 0) => {
      setAttempts(n + 1);
      try {
        const r = await axios.get(`${API}/api/checkout/status/${sessionId}`, { withCredentials: true });
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (r.data.status === "expired") {
          setState("failed");
          return;
        }
        if (n >= 8) {
          setState("timeout");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (n >= 8) {
          setState("failed");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
  }, [sessionId]);

  // Pacchetti che generano account socio + welcome email con credenziali
  const isTessera = !!(data?.package && /tessera|premium|corso/i.test(data.package));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7ff] via-white to-[#eef2ff] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-8 border border-slate-200">
        {state === "polling" && (
          <div className="text-center py-8" data-testid="checkout-polling">
            <Loader2 className="h-12 w-12 text-[#102a6b] animate-spin mx-auto mb-5" />
            <h1 className="text-2xl font-extrabold text-[#0a1f55]">Stiamo confermando il pagamento…</h1>
            <p className="mt-2 text-[14px] text-slate-600">Tentativo {attempts}/8. Non chiudere questa pagina.</p>
          </div>
        )}

        {state === "success" && (
          <div className="text-center" data-testid="checkout-success">
            <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-5 shadow-xl shadow-emerald-500/30">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h1 className="text-3xl font-extrabold text-[#0a1f55]">Pagamento confermato!</h1>
            <p className="mt-3 text-[14.5px] text-slate-600">
              Grazie per il pagamento di <strong className="text-[#0a1f55]">{data?.package}</strong>.
              Stiamo inviando la <strong>ricevuta in PDF</strong> alla tua email.
            </p>

            <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-left space-y-2 text-[13.5px]">
              <div className="flex justify-between"><span className="text-slate-600">Pacchetto</span><span className="font-bold text-[#0a1f55]">{data?.package}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Importo</span><span className="font-extrabold text-emerald-700">€ {((data?.amount_total || 0) / 100).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">ID Transazione</span><span className="font-mono text-[11.5px] text-slate-500 truncate max-w-[60%]">{sessionId?.slice(0, 24)}…</span></div>
            </div>

            {isTessera ? (
              <>
                <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#0a1f55] to-[#102a6b] text-white p-5 text-left" data-testid="tessera-welcome-panel">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center flex-shrink-0">
                      <Key className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-[#fbbf24]">Benvenuto/a in A.C.D.T.</div>
                      <h3 className="mt-1 text-lg font-extrabold">Controlla la tua email</h3>
                      <p className="mt-1.5 text-[13px] text-slate-200 leading-relaxed">
                        Ti abbiamo inviato le <strong className="text-white">credenziali di accesso</strong> alla dashboard.
                        Al primo login troverai la <strong className="text-white">tessera digitale con QR</strong>,
                        il regalo di benvenuto e tutte le tue risorse.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-3 text-left">
                  <a
                    href={`${API}/api/download/pillole-tutela-digitale`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="download-pillole-tutela"
                    className="group rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 p-4 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-amber-800 text-[10.5px] font-extrabold uppercase tracking-widest">
                      <FileDown className="h-3.5 w-3.5" /> Regalo di benvenuto
                    </div>
                    <div className="mt-1.5 text-[13.5px] font-extrabold text-[#0a1f55]">
                      Pillole della Tutela Digitale
                    </div>
                    <div className="mt-1 text-[11.5px] text-amber-900">
                      Scarica il PDF gratuito →
                    </div>
                  </a>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center gap-2 text-[#f59e0b] text-[10.5px] font-extrabold uppercase tracking-widest">
                      <Scale className="h-3.5 w-3.5" /> Consulenza legale
                    </div>
                    <div className="mt-1.5 text-[13.5px] font-extrabold text-[#0a1f55]">
                      Appuntamento con avvocato
                    </div>
                    <div className="mt-1 text-[11.5px] text-slate-600">
                      Richiedilo dalla tua Area Riservata
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="mt-6 rounded-xl bg-[#fef3c7] border border-amber-200 p-4 flex items-start gap-3 text-left">
                <Mail className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-[12.5px] text-amber-900">
                  <strong>Controlla la tua casella di posta</strong>: trovi la ricevuta PDF con i dati di fatturazione che hai inserito. Se non la vedi, guarda nello spam.
                </div>
              </div>
            )}

            <div className="mt-7 flex gap-2 justify-center flex-wrap">
              <Button variant="outline" asChild>
                <Link to="/" data-testid="success-home">Torna alla home</Link>
              </Button>
              <Button onClick={() => navigate("/dashboard")} data-testid="success-dashboard" className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                <Award className="mr-2 h-4 w-4" /> Vai alla Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {state === "timeout" && (
          <div className="text-center py-4" data-testid="checkout-timeout">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-5" />
            <h1 className="text-2xl font-extrabold text-[#0a1f55]">Verifica in corso</h1>
            <p className="mt-2 text-[14px] text-slate-600">
              Stiamo ancora verificando il pagamento con Stripe. Se non arriva la conferma via email entro pochi minuti, contatta la segreteria.
            </p>
            <Button onClick={() => window.location.reload()} className="mt-5 bg-[#102a6b] text-white">Riprova verifica</Button>
          </div>
        )}

        {state === "failed" && (
          <div className="text-center py-4" data-testid="checkout-failed">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-5" />
            <h1 className="text-2xl font-extrabold text-[#0a1f55]">Pagamento non confermato</h1>
            <p className="mt-2 text-[14px] text-slate-600">
              Non siamo riusciti a confermare il pagamento. Se l&apos;importo è stato addebitato, sarà rimborsato automaticamente entro pochi giorni.
            </p>
            <Button asChild className="mt-5 bg-[#102a6b] text-white">
              <Link to="/">Torna alla home</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
